import { useEffect } from 'react';
import { ShieldOff, Clock, CreditCard } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useSignalR } from '@/hooks/useSignalR';
import { billingApi } from '@/api/billingApi';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function AccountGate({ children }: { children: React.ReactNode }) {
  const billingStatus = useAuthStore((s) => s.billingStatus);
  const trialEndsOn = useAuthStore((s) => s.trialEndsOn);
  const accountSuspended = useAuthStore((s) => s.accountSuspended);
  const token = useAuthStore((s) => s.token);
  const setBillingInfo = useAuthStore((s) => s.setBillingInfo);
  const setAccountSuspended = useAuthStore((s) => s.setAccountSuspended);
  const { on, off } = useSignalR();

  const GRACE_PERIOD_DAYS = 3;

  const trialExpiredDate = trialEndsOn ? new Date(trialEndsOn) : null;
  const gracePeriodEndDate = trialExpiredDate ? new Date(trialExpiredDate.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000) : null;
  const now = new Date();

  const isTrialExpiredButInGrace =
    billingStatus === 'Trial' &&
    trialExpiredDate !== null &&
    trialExpiredDate <= now &&
    gracePeriodEndDate !== null &&
    now < gracePeriodEndDate;

  const isTrialExpiredPastGrace =
    billingStatus === 'Trial' &&
    trialExpiredDate !== null &&
    gracePeriodEndDate !== null &&
    now >= gracePeriodEndDate;

  const isSubscriptionIssue =
    billingStatus === 'PastDue' || billingStatus === 'Cancelled';

  useEffect(() => {
    if (!token) return;

    const handler = (data: unknown) => {
      const ev = data as { eventType?: string };
      if (ev?.eventType === 'Suspended') {
        setAccountSuspended(true);
      } else if (ev?.eventType === 'Reactivated') {
        setAccountSuspended(false);
      }
    };

    on('AccountStatusChanged', handler);
    return () => off('AccountStatusChanged', handler);
  }, [token, on, off, setAccountSuspended]);

  useEffect(() => {
    if (!token) return;

    const poll = () => {
      billingApi.getStatus()
        .then((d) => {
          setBillingInfo(d.billingStatus, d.trialEndsOn, d.subscriptionRenewalOn);
          setAccountSuspended(d.isSuspended);
        })
        .catch(() => {});
    };

    const id = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [token, setBillingInfo, setAccountSuspended]);

  if (accountSuspended) {
    return (
      <LockedScreen
        icon={<ShieldOff />}
        title="Account Suspended"
        message="Your account has been suspended by an administrator. Please contact support@wrenchlab.net for assistance."
      />
    );
  }

  if (isTrialExpiredPastGrace) {
    return (
      <LockedScreen
        icon={<Clock />}
        title="Your Free Trial Has Ended"
        message="Upgrade to a paid plan to continue using WrenchLab."
        cta={{ label: 'View Plans', href: 'https://wrenchlab.net/pricing' }}
        secondaryLink={{ label: 'Back to Staff Login', href: '/staff-login' }}
      />
    );
  }

  if (isSubscriptionIssue) {
    return (
      <LockedScreen
        icon={<CreditCard />}
        title="Subscription Required"
        message="Your subscription is inactive. Renew to restore full access to WrenchLab."
        cta={{ label: 'Renew Subscription', href: 'https://wrenchlab.net/pricing' }}
      />
    );
  }

  return <>{children}</>;
}

function LockedScreen({
  icon,
  title,
  message,
  cta,
  secondaryLink,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
  cta?: { label: string; href: string };
  secondaryLink?: { label: string; href: string };
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-neutral-50 overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
        <img src="/logo.png" alt="" className="h-96 w-96 object-contain" />
      </div>
      <div className="relative z-10 max-w-md w-full text-center px-8">
        <div className="flex justify-center mb-6 text-neutral-300">
          <div className="[&>svg]:w-16 [&>svg]:h-16">{icon}</div>
        </div>
        <h1 className="text-2xl font-bold text-neutral-900 mb-3">{title}</h1>
        <p className="text-neutral-500 mb-8 leading-relaxed">{message}</p>
        {cta && (
          <a
            href={cta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary-600 text-white rounded-wl px-6 py-3 font-semibold hover:bg-primary-700 transition-colors"
          >
            {cta.label} →
          </a>
        )}
        {secondaryLink && (
          <div className="mt-6">
            <a
              href={secondaryLink.href}
              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium text-sm"
            >
              {secondaryLink.label} ←
            </a>
          </div>
        )}
        <div className="mt-10 pt-6 border-t border-neutral-200">
          <p className="text-xs text-neutral-400">
            Need help?{' '}
            <a href="mailto:support@wrenchlab.net" className="text-primary-600 hover:underline">
              support@wrenchlab.net
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
